/**
 * Presentational results panel for global search. Receives controller
 * outputs as props so both the sidebar dropdown and the Cmd+K palette
 * render identical content.
 */
import { useEffect, useRef } from 'react';
import { CompanyLogo } from '@/components/ui/CompanyLogo';
import { normalizePersonName } from '@/utils/displayName';
import styles from './GlobalSearch.module.css';
import type {
  GlobalSearchSections,
  SearchItem,
} from './useGlobalSearchController';
import type { UseGlobalSearchResult } from './useGlobalSearch';

export interface GlobalSearchResultsProps {
  search: UseGlobalSearchResult;
  sections: GlobalSearchSections;
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  onSelectItem: (item: SearchItem) => void;
  /** ID used for ARIA combobox wiring on the input. */
  listboxId: string;
}

function PersonAvatar({ photoUrl, name }: { photoUrl?: string | null; name: string }) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        className={styles.avatarImg}
        loading="lazy"
      />
    );
  }
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((t) => t.charAt(0).toUpperCase())
    .join('') || '?';
  return <div className={styles.avatarFallback}>{initials}</div>;
}

function ItemRow({
  item,
  active,
  onSelect,
  onHover,
}: {
  item: SearchItem;
  active: boolean;
  onSelect: () => void;
  onHover: () => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (active && ref.current) {
      ref.current.scrollIntoView({ block: 'nearest' });
    }
  }, [active]);

  let avatar: React.ReactNode;
  let primary: string;
  let secondary: string | null = null;

  if (item.kind === 'company') {
    const c = item.data;
    avatar = (
      <CompanyLogo
        logoUrl={c.logo_url}
        domain={c.domain}
        name={c.name}
        size={28}
      />
    );
    primary = c.display_name?.trim() || c.name;
    secondary =
      [c.jurisdiction_code, c.registration_number].filter(Boolean).join(' · ') ||
      null;
  } else if (item.kind === 'person') {
    const p = item.data;
    const personName = normalizePersonName(p.canonical_name);
    avatar = <PersonAvatar photoUrl={p.photo_url} name={personName} />;
    primary = personName;
    secondary =
      [p.current_title, p.current_org_name].filter(Boolean).join(' · ') || null;
  } else {
    const r = item.data;
    avatar =
      r.kind === 'company' ? (
        <CompanyLogo
          logoUrl={r.logoUrl}
          domain={r.domain}
          name={r.name}
          size={28}
        />
      ) : (
        <PersonAvatar photoUrl={r.photoUrl} name={r.name} />
      );
    primary = r.name;
    secondary = r.subtitle ?? null;
  }

  return (
    <button
      ref={ref}
      type="button"
      role="option"
      id={`gsearch-item-${item.flatIndex}`}
      aria-selected={active}
      className={`${styles.resultRow} ${active ? styles.resultRowActive : ''}`}
      onMouseDown={(e) => {
        // mousedown (not click) so the blur-on-click race doesn't close
        // the popover before navigation lands.
        e.preventDefault();
        onSelect();
      }}
      onMouseEnter={onHover}
    >
      <div className={styles.resultAvatar}>{avatar}</div>
      <div className={styles.resultText}>
        <div className={styles.resultPrimary}>{primary}</div>
        {secondary && <div className={styles.resultSecondary}>{secondary}</div>}
      </div>
    </button>
  );
}

export function GlobalSearchResults({
  search,
  sections,
  activeIndex,
  setActiveIndex,
  onSelectItem,
  listboxId,
}: GlobalSearchResultsProps) {
  // Three states drive what we render:
  //   1. query is empty           → recents (or empty hint)
  //   2. query enabled, no data   → loading / no-results
  //   3. query enabled with data  → companies + people sections
  if (!search.isEnabled) {
    if (sections.recents.length === 0) {
      return (
        <div className={styles.dropdown} role="listbox" id={listboxId}>
          <div className={styles.hint}>
            Search companies and people. Type at least 2 characters.
          </div>
        </div>
      );
    }
    return (
      <div className={styles.dropdown} role="listbox" id={listboxId}>
        <div className={styles.sectionLabel}>Recent</div>
        {sections.recents.map((item) => (
          <ItemRow
            key={`recent-${item.data.kind}-${(item.data as { id: string }).id}`}
            item={item}
            active={item.flatIndex === activeIndex}
            onSelect={() => onSelectItem(item)}
            onHover={() => setActiveIndex(item.flatIndex)}
          />
        ))}
      </div>
    );
  }

  if (search.isError) {
    return (
      <div className={styles.dropdown} role="listbox" id={listboxId}>
        <div className={styles.error}>Search unavailable. Try again.</div>
      </div>
    );
  }

  if (!search.hasResults) {
    return (
      <div className={styles.dropdown} role="listbox" id={listboxId}>
        <div className={styles.hint}>
          {search.isLoading
            ? 'Searching…'
            : `No matches for “${search.debouncedQuery}”.`}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dropdown} role="listbox" id={listboxId}>
      {sections.companies.length > 0 && (
        <>
          <div className={styles.sectionLabel}>Companies</div>
          {sections.companies.map((item) => (
            <ItemRow
              key={`co-${item.data.id}`}
              item={item}
              active={item.flatIndex === activeIndex}
              onSelect={() => onSelectItem(item)}
              onHover={() => setActiveIndex(item.flatIndex)}
            />
          ))}
        </>
      )}
      {sections.people.length > 0 && (
        <>
          <div className={styles.sectionLabel}>People</div>
          {sections.people.map((item) => (
            <ItemRow
              key={`p-${item.data.persona_id}`}
              item={item}
              active={item.flatIndex === activeIndex}
              onSelect={() => onSelectItem(item)}
              onHover={() => setActiveIndex(item.flatIndex)}
            />
          ))}
        </>
      )}
    </div>
  );
}
